import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AnimationOptions } from 'ngx-lottie';
import { Subscription } from 'rxjs';
import { LocalStorageKeys } from 'src/app/core/local-storage/local-storage.enum';
import { LocalStorageService } from 'src/app/core/local-storage/local-storage.service';
import { LocaleEnum } from 'src/app/submit-lecture/lecture.values';
import { LeaderBoardComponent } from 'src/app/views/pages/lectures/leader-board/leader-board.component';
import { ScoreBoardComponent } from 'src/app/views/pages/lectures/score-board/score-board.component';
import { ProgressStageData, ProgressSteps, ProgressLastStep } from './progress.values';

/**
 * @title Configurable progress-bar
 */

const LAST_STEP_VAL = 20;
@Component({
  selector: 'progress-bar',
  templateUrl: 'progress-bar.component.html',
  styleUrls: ['progress-bar.component.scss'],
})
export class ProgressBarComponent implements OnInit, OnDestroy, OnChanges {
  @Input() numClosedLectures;
  private subscription: Subscription;
  private lang: string;

  currentPlanet: ProgressStageData;
  nextPlanet: ProgressStageData;
  lectsToGo: number;
  lectsTotalLeg: number;
  dir: string;
  value = 0;
  isLast = false;
  isLevelReached = false;

  options: AnimationOptions = {
    path: '/assets/animations/falling-stars.json',
    loop: true
  };

  constructor(
    private localStorageService: LocalStorageService,
    private modalService: NgbModal,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    const lang: string = this.localStorageService.read(LocalStorageKeys.language) ?? LocaleEnum.HE;
    this.onLangChange(lang);

    this.subscription = this.translate.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.onLangChange(event.lang);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(): void {
    if (this.numClosedLectures !== undefined) {
      this.calculateValue();
    }
  }

  onProgressInfoClick(): void {
    this.modalService.open(ScoreBoardComponent, {
      scrollable: true,
      size: 'lg'
    });
  }

  onLeaderBoardClick(): void {
    this.modalService.open(LeaderBoardComponent, {
      scrollable: true,
      size: 'lg',
      // windowClass: 'modal-lg-xl'
    });
  }

  private onLangChange(lang): void {
    this.lang = lang;
    this.dir = this.lang === LocaleEnum.HE ? 'rtl' : 'ltr';
  }

  private calculateValue(): void {
    for (let i = 0; i < ProgressSteps.length; i++) {
      if (ProgressSteps[i].numLect > this.numClosedLectures) {
        const newNextPlanet = ProgressSteps[i];
        if (this.nextPlanet && this.nextPlanet?.stage !== newNextPlanet?.stage) {
          this.isLevelReached = true;
          setTimeout(() => {
            this.isLevelReached = false;
          }, 10000);
        }
        this.currentPlanet = ProgressSteps[i - 1];
        this.nextPlanet = ProgressSteps[i];
        const traveledLength = this.numClosedLectures;
        this.lectsTotalLeg = (this.nextPlanet?.numLect ?? 0) - (this.currentPlanet?.numLect ?? 0);
        this.lectsToGo = this.lectsTotalLeg - (traveledLength - (this.currentPlanet?.numLect ?? 0));
        this.value = (traveledLength - (this.currentPlanet?.numLect ?? 0)) / this.lectsTotalLeg * 100;
        break;
      }
    }
    if (!this.nextPlanet) { // no step found, set last step
      this.isLast = true;
      this.currentPlanet = ProgressSteps[ProgressSteps.length - 1];
      this.nextPlanet = ProgressLastStep;
      this.value = LAST_STEP_VAL;
    }
  }

  getFirstPlanetIcon(): string {
    const planet = this.currentPlanet?.stage?.toLowerCase();
    return planet ? `assets/images/progress/${planet}.svg` : null;
  }

  getSecondPlanetIcon(): string {
    const planet = this.nextPlanet?.stage?.toLowerCase();
    return planet ? `assets/images/progress/${planet}.svg` : null;
  }
}
